import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { ArrowRight, Plant, Newspaper, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";

const WPM = 200;

function readingMinutes(wordCount) {
  if (!wordCount) return null;
  return Math.max(1, Math.round(wordCount / WPM));
}

function fmtDate(s) {
  if (!s) return "";
  try {
    return new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "";
  }
}

export default function Blog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/blog?limit=30");
        setItems(data.items || []);
      } catch {
        toast.error("Couldn't load the blog");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-sand-50" data-testid="blog-page">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-14">
        <div className="text-center max-w-2xl mx-auto">
          <div className="font-mono-data text-mute inline-flex items-center gap-2 justify-center">
            <Newspaper size={14} weight="duotone" /> field notes
          </div>
          <h1 className="font-display text-5xl md:text-6xl text-ink leading-tight mt-2">
            Field notes for a <em className="italic text-terra">warming</em> garden.
          </h1>
          <p className="text-mute mt-4 leading-relaxed">
            Practical, climate-aware reads from the A Must For Earth team — heat stress, humidity,
            UV, drought, and the small habit changes that protect your plants.
          </p>
        </div>

        <div className="mt-12">
          {loading ? (
            <div className="font-mono-data text-mute animate-pulse-soft">loading posts…</div>
          ) : items.length === 0 ? (
            <div className="bg-sand-100 border border-sand-200 rounded-3xl p-12 text-center" data-testid="blog-empty">
              <Plant size={48} weight="duotone" className="text-forest mx-auto mb-4" />
              <h3 className="font-display text-3xl">No posts yet</h3>
              <p className="text-mute mt-2">Check back soon — we&apos;re growing this corner of the site.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
              {items.map((p) => (
                <BlogCard key={p.slug} post={p} />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function BlogCard({ post }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      data-testid={`blog-card-${post.slug}`}
      className="group block bg-sand-100 border border-sand-200 rounded-3xl overflow-hidden hover:shadow-soft transition"
    >
      <div className="aspect-[16/9] bg-ink/5 overflow-hidden">
        {post.image_b64 ? (
          <img
            src={`data:image/png;base64,${post.image_b64}`}
            alt={post.title}
            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-mute">
            <Plant size={36} weight="duotone" />
          </div>
        )}
      </div>
      <div className="p-5">
        <div className="font-mono-data text-mute text-xs flex items-center gap-2">
          <span>{fmtDate(post.created_at)}</span>
          {readingMinutes(post.word_count) && (
            <>
              <span aria-hidden>·</span>
              <span className="inline-flex items-center gap-1">
                <Clock size={11} weight="duotone" /> {readingMinutes(post.word_count)} min
              </span>
            </>
          )}
        </div>
        <h3 className="font-display text-2xl text-ink mt-1 leading-snug">{post.title}</h3>
        {post.excerpt && <p className="text-mute mt-2 text-sm leading-relaxed line-clamp-3">{post.excerpt}</p>}
        <div className="mt-4 flex items-center justify-between">
          {post.tags && post.tags.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {post.tags.slice(0, 3).map((t, i) => (
                <span key={i} className="tag severity-mild">{t}</span>
              ))}
            </div>
          ) : <span />}
          <span className="text-forest text-sm inline-flex items-center gap-1 group-hover:gap-2 transition-all">
            Read <ArrowRight size={14} weight="bold" />
          </span>
        </div>
      </div>
    </Link>
  );
}
